import React from 'react';
import { Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';

export type PersonnelFilterOptions = {
  id: string;
  name: string;
  phone: string;
  status: number;
};

export const DEFAULT_PERSONNEL_FILTERS: PersonnelFilterOptions = {
  id: '',
  name: '',
  phone: '',
  status: 1,
};

const STATUS_VALUES = [2, 1, 0] as const;

interface Props {
  filters: PersonnelFilterOptions;
  setFilters: (filters: PersonnelFilterOptions) => void;
}

const PersonnelFilterArea: React.FC<Props> = ({ filters, setFilters }) => {
  const { t } = useTranslation();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();

  const statusLabel = (v: number) => {
    if (v === 2) return t('central.platformPermission.personnel.filters.statusAll');
    if (v === 1) return t('central.platformPermission.personnel.filters.statusActive');
    return t('central.platformPermission.personnel.filters.statusInactive');
  };

  const statusOptions = STATUS_VALUES.map((v) => ({
    value: v,
    label: statusLabel(v),
  }));

  const labelId = String(t('central.platformPermission.personnel.filters.id'));
  const labelName = String(t('central.platformPermission.personnel.filters.name'));
  const labelPhone = String(t('central.platformPermission.personnel.filters.phone'));
  const labelStatus = String(t('central.platformPermission.personnel.filters.status'));
  const colLabels = [labelId, labelName, labelPhone, labelStatus];

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-4 items-start">
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(colLabels)}>{labelId}</span>
            <div className={inputContainerClassName}>
              <Input
                value={filters.id}
                onChange={(e) => setFilters({ ...filters, id: e.target.value })}
                allowClear
                size="middle"
                className="w-full"
                placeholder={t('components.input.typeIn') as string}
              />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(colLabels)}>{labelName}</span>
            <div className={inputContainerClassName}>
              <Input
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                allowClear
                size="middle"
                className="w-full"
                placeholder={t('components.input.typeIn') as string}
              />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(colLabels)}>{labelPhone}</span>
            <div className={inputContainerClassName}>
              <Input
                value={filters.phone}
                onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
                allowClear
                size="middle"
                className="w-full"
                placeholder={t('components.input.typeIn') as string}
              />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className={containerClassName}>
            <span className={getSmartLabelClassName(colLabels)}>{labelStatus}</span>
            <div className={inputContainerClassName}>
              <Select
                value={filters.status}
                onChange={(v) => setFilters({ ...filters, status: v })}
                options={statusOptions}
                size="middle"
                className="w-full"
                placeholder={t('components.input.select') as string}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonnelFilterArea;
